
<template>
  <div
    style="
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background-color: var(--color-bg);
      border-radius: 10px;
      box-shadow: 0 0 20px 0 var(--color-overlay);
    "
  >
    <v-data-table
      style="flex: 1; overflow: hidden"
      :headers="headers"
      :items="tableData"
      item-value="name"
      class="elevation-0"
      :hide-default-footer="true"
      :items-per-page="50"
    >
      <!-- CUSTOM HEADER SLOTS -->
      <template v-slot:header.name="{ header }">
        <span style="color: var(--color-text)">
          {{ header.text }}
        </span>
      </template>

      <template v-slot:header.yearToDate="{ header }">
        <span
          class="d-flex align-center"
          style="color: var(--color-text)"
        >
          {{ header.text }}
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="ml-1"
                v-bind="attrs"
                :color="'var(--color-text)'"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>
              The cumulative CII calculated from the start of the
              calendar year to the present date.
            </span>
          </v-tooltip>
        </span>
      </template>

      <template v-slot:header.rollingTwelveMonths="{ header }">
        <span style="color: var(--color-text)">
          {{ header.text }}
        </span>
      </template>

      <template v-slot:header.input="{ header }">
        <span style="color: var(--color-text)">
          {{ header.text }}
        </span>
      </template>

      <!-- BODY (UNCHANGED) -->
      <template v-slot:body="{}">
        <DataRow
          name="Distance"
          :year-to-date="displayRowData('distance', 'yearToDate')"
          :rolling-twelve-months="displayRowData('distance', 'rollingTwelveMonths')"
          :input="displayRowData('distance', 'input')"
          :is-alternate="true"
          :input-is-loading="isLoading"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Average Laden Speed"
          :year-to-date="displayRowData('averageLadenSpeed', 'yearToDate')"
          :rolling-twelve-months="displayRowData('averageLadenSpeed', 'rollingTwelveMonths')"
          :input="Number(displayRowData('averageLadenSpeed', 'input'))"
          :on-change="onLadenSpeedChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Average Ballast Speed"
          :year-to-date="displayRowData('averageBallastSpeed', 'yearToDate')"
          :rolling-twelve-months="displayRowData('averageBallastSpeed', 'rollingTwelveMonths')"
          :input="Number(displayRowData('averageBallastSpeed', 'input'))"
          :is-alternate="true"
          :on-change="onBallastSpeedChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <DataRow
          name="Average Laden MeFoc"
          :year-to-date="displayRowData('averageLadenMeFoc', 'yearToDate')"
          :rolling-twelve-months="displayRowData('averageLadenMeFoc', 'rollingTwelveMonths')"
          :input="displayRowData('averageLadenMeFoc', 'input')"
          :input-is-loading="isLoading"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <DataRow
          name="Average Ballast MeFoc"
          :year-to-date="displayRowData('averageBallastMeFoc', 'yearToDate')"
          :rolling-twelve-months="displayRowData('averageBallastMeFoc', 'rollingTwelveMonths')"
          :input="displayRowData('averageBallastMeFoc', 'input')"
          :is-alternate="true"
          :input-is-loading="isLoading"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Excess Foc Due to Weather"
          :year-to-date="displayRowData('excessFocDueToWeather', 'yearToDate')"
          :rolling-twelve-months="displayRowData('excessFocDueToWeather', 'rollingTwelveMonths')"
          :input="Number(displayRowData('excessFocDueToWeather', 'input'))"
          :on-change="onExcessFocDueToWeatherChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Average Fouling"
          :year-to-date="displayRowData('averageFouling', 'yearToDate')"
          :rolling-twelve-months="displayRowData('averageFouling', 'rollingTwelveMonths')"
          :input="Number(displayRowData('averageFouling', 'input'))"
          :is-alternate="true"
          :on-change="onAverageFoulingChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Laden Percentage Time"
          :year-to-date="displayRowData('ladenPercentageTime', 'yearToDate')"
          :rolling-twelve-months="displayRowData('ladenPercentageTime', 'rollingTwelveMonths')"
          :input="Number(displayRowData('ladenPercentageTime', 'input'))"
          :on-change="onLadenPercentageTimeChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Ballast Percentage Time"
          :year-to-date="displayRowData('ballastPercentageTime', 'yearToDate')"
          :rolling-twelve-months="displayRowData('ballastPercentageTime', 'rollingTwelveMonths')"
          :input="Number(displayRowData('ballastPercentageTime', 'input'))"
          :is-alternate="true"
          :on-change="onBallastPercentageTimeChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Idle Percentage Time"
          :year-to-date="displayRowData('idlePercentageTime', 'yearToDate')"
          :rolling-twelve-months="displayRowData('idlePercentageTime', 'rollingTwelveMonths')"
          :input="Number(displayRowData('idlePercentageTime', 'input'))"
          :on-change="onIdlePercentageTimeChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <InputDataRow
          name="Aux CO2 Contribution Percentage"
          :year-to-date="displayRowData('auxCo2ContributionPercentage', 'yearToDate')"
          :rolling-twelve-months="displayRowData('auxCo2ContributionPercentage', 'rollingTwelveMonths')"
          :input="Number(displayRowData('auxCo2ContributionPercentage', 'input'))"
          :is-alternate="true"
          :on-change="onAuxCo2ContributionPercentageChanged"
          :contribution-values-loading="ciiContributionValuesLoading"
        />

        <DataRow
          name="CII"
          :year-to-date="displayCii(displayRowData('cii', 'yearToDate'))"
          :rolling-twelve-months="displayCii(displayRowData('cii', 'rollingTwelveMonths'))"
          :input="displayCii(displayRowData('cii', 'input'))"
          :input-is-loading="isLoading"
          :contribution-values-loading="ciiContributionValuesLoading"
          :text-color="getColor(displayRowData('cii', 'yearToDate'))"
        />
      </template>
    </v-data-table>

    <div style="display:flex;justify-content:center;align-items:center;flex-direction:column;gap:2px;padding:1em;">
      <v-btn
        :loading="calculationLoading"
        color="primary"
        @click="onCalculateButtonPressed"
      >
        Calculate
      </v-btn>

      <v-btn
        :disabled="isLoading"
        color="grey"
        plain
        class="mt-2"
        @click="onResetToDefaultButtonPressed"
      >
        Reset to default
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import DataRow from "./components/DataRow.vue";
import InputDataRow from "./components/InputDataRow.vue";
import getCiiGrade from "../../modules/cii/utils/cii_grade";

export default {
  name: "DataTable",

  components: {
    DataRow,
    InputDataRow,
  },

  data() {
    return {
      headers: [
        { text: "Name", value: "name" },
        { text: "Year to Date", value: "yearToDate", sortable: false},
        { text: "Rolling 12 Months", value: "rollingTwelveMonths" },
        { text: "Forecast till EOY", value: "input" },
      ],
    };
  },

  computed: {
    ...mapState("cii", [
      "ciiRatings",
      "ciiContributionValues",
      "ciiContributionValuesLoading",
    ]),
    ...mapGetters("cii", ["tableData"]),
    ...mapState("cii/calculation", [
      "calculationLoading",
      "calculationError",
      "ciiCalculationInput",
    ]),
    isLoading() {
      return this.ciiContributionValuesLoading || this.calculationLoading;
    },
  },

  methods: {
    ...mapActions("cii/calculation", ["calculateCiiContribution"]),

    displayRowData(key, type) {
      try {
        return this.ciiContributionValues[key][type].toFixed(2);
      } catch (error) {
        return "-";
      }
    },

    displayCii(value) {
      try {
        return `${value} ( ${getCiiGrade(value, this.ciiRatings)} )`;
      } catch (error) {
        return "-";
      }
    },

    getColor(value) {
      try {
        const ciiGrade = getCiiGrade(value, this.ciiRatings);
        if (ciiGrade === "A") return "green";
        if (ciiGrade === "B") return "orange";
        return "red";
      } catch (error) {
        return "black";
      }
    },

    onCalculateButtonPressed() {
      this.calculateCiiContribution(this.$route.params.vessel);
    },

    onResetToDefaultButtonPressed() {
      this.$store.commit("cii/RESET_CII_CONTRIBUTION_VALUES");
    },

    onLadenSpeedChanged(value) {
      this._onAttributeChanged("averageLadenSpeed", Number(value));
    },

    onBallastSpeedChanged(value) {
      this._onAttributeChanged("averageBallastSpeed", Number(value));
    },

    onAuxCo2ContributionPercentageChanged(value) {
      this._onAttributeChanged("auxCO2Contribution", Number(value));
    },

    onExcessFocDueToWeatherChanged(value) {
      this._onAttributeChanged("averageExcessFocDueToWeatherPerDay", Number(value));
    },

    onAverageFoulingChanged(value) {
      this._onAttributeChanged("averageFouling", Number(value));
    },

    onLadenPercentageTimeChanged(value) {
      this._onAttributeChanged("ladenTimePercentage", Number(value));
    },

    onBallastPercentageTimeChanged(value) {
      this._onAttributeChanged("ballastTimePercentage", Number(value));
    },

    onIdlePercentageTimeChanged(value) {
      this._onAttributeChanged("idleTimePercentage", Number(value));
    },

    _onAttributeChanged(attribute, value) {
      this.$store.commit(
        "cii/calculation/SET_CII_CALCULATION_INPUT_ATTRIBUTE_STATE",
        { name: attribute, value: value }
      );
    },
  },
};
</script>

<style scoped>
.v-data-table {
  background-color: var(--color-bg) !important;
  color: var(--color-text) !important;
}
</style>
